import {
  Grid,
  Theme,
  Typography,
  WithStyles,
  withStyles
} from '@material-ui/core'
import LeftIcon from 'mdi-react/ChevronLeftIcon'
import RightIcon from 'mdi-react/ChevronRightIcon'
import moment, { Moment } from 'moment-timezone'
import { useEffect, useState } from 'react'
import { WithTranslation, withTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { formValueSelector } from 'redux-form'
import {
  selectAvailabilitiesByApartment,
  selectAvailabilitiesByApplicationIds
} from '../../../../../selectors/applicantAvailability'
import { selectSelectedApplications } from '../../../../../selectors/applications'
import { selectContentIdFromPathname } from '../../../../../selectors/router'
import { selectShowingsInterfaceByApartmentId } from '../../../../../selectors/showings'
import { getCurrentLanguage } from '../../../../../translations/i18n'
import { ApplicantAvailability } from '../../../../../types/applicantAvailability'
import { Showing } from '../../../../../types/showings'
import {
  EmptyShowingsInterface,
  StateInterface
} from '../../../../../types/states'
import {
  getFutureAvalibilities,
  getPastAvalibilities
} from '../../../../../utils/applicantAvailabilities'
import { getFutureShowings } from '../../../../../utils/showings/filterAndSearch'

const styles = (_theme: Theme) => ({
  calendarContainer: {
    display: 'flex',
    justifyContent: 'space-around',
    overflow: 'hidden',
    width: '100%'
  },
  customCalendarContainer: {
    background: '#E7ECEF',
    borderRadius: '27px',
    padding: '21px 10px 15px 10px'
  },
  horizontalList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
    width: '100%',
    padding: ' 1.2rem 0rem 0rem 0rem',
    // span rule also centers the day number inside the date box — keep even though grid ignores flex
    '& span': {
      flex: '1 1 calc(100% / 7)',
      textAlign: 'center'
    },
    '& div': {
      textAlign: 'center',
      flex: '1 1 calc(100% / 7)'
    }
  },
  listItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    cursor: 'pointer',
    justifyContent: 'space-between',
    padding: '5px 0'
  },
  inActivelistItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    cursor: 'pointer',
    justifyContent: 'space-between',
    color: '#919DB5',
    padding: '5px 0',
    '& div': {
      border: '2px solid #EFF2F5',
      background: '#EFF2F5'
    }
  },
  date: {
    padding: '0.65rem 0',
    width: '59px',
    maxWidth: '100%',
    boxSizing: 'border-box' as const,
    display: 'flex',
    borderRadius: '7px',
    border: '3px solid #FFFFFF',
    background: '#FFFFFF'
  },
  existingDate: {
    padding: '0.65rem 0',
    width: '59px',
    maxWidth: '100%',
    boxSizing: 'border-box' as const,
    display: 'flex',
    borderRadius: '7px',
    border: '3px solid #4F9B44',
    color: '#4F9B44',
    background: '#fff'
  },
  selectedDate: {
    padding: '0.65rem 0',
    width: '59px',
    maxWidth: '100%',
    boxSizing: 'border-box' as const,
    display: 'flex',
    borderRadius: '7px',
    border: '3px solid #276C1E',
    background: '#276C1E',
    color: '#ffffff'
  },
  existingShowing: {
    padding: '0.65rem 0',
    width: '59px',
    maxWidth: '100%',
    boxSizing: 'border-box' as const,
    display: 'flex',
    borderRadius: '7px',
    border: '3px solid #4F9B44',
    background: '#4F9B44',
    color: '#ffffff'
  },
  disabledDate: {
    padding: '0.65rem 0',
    width: '59px',
    maxWidth: '100%',
    boxSizing: 'border-box' as const,
    display: 'flex',
    borderRadius: '7px',
    color: '#919DB5',
    border: '2px solid #EFF2F5',
    background: '#EFF2F5'
  },

  dateText: {
    'text-transform': 'uppercase',
    fontSize: '0.8rem',
    color: '#919DB5'
  },
  today: {
    zIndex: 99 as const,
    fontSize: '8px',
    position: 'absolute' as const,
    top: '3px',
    color: '#000'
  },
  selectedToday: {
    zIndex: 99 as const,
    fontSize: '8px',
    position: 'absolute' as const,
    top: '3px',
    color: '#fff'
  },
  container: {
    'box-sizing': 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: '1.2rem'
  },
  arrows: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#919DB5'
  },
  monthContainer: {
    display: 'flex',
    position: 'relative' as const
  },
  month: {
    margin: '0rem 1.2rem',
    width: '120px',
    fontSize: '1.2rem',
    textAlign: 'center' as const,
    'text-transform': 'capitalize'
  },
  futureAvaliabilities: {
    position: 'absolute' as const,
    color: '#fff',
    width: '25px',
    padding: '2px 0',
    textAlign: 'center' as const,
    right: '0',
    background: '#4F9B44',
    borderRadius: '50%'
  },
  pastAvaliabilities: {
    position: 'absolute' as const,
    color: '#fff',
    width: '25px',
    padding: '2px 0',
    textAlign: 'center' as const,
    left: '0',
    background: '#4F9B44',
    borderRadius: '50%'
  }
})

interface CustomCalendarProps {
  input: any
  meta: any
  label: string
  custom: any
}

interface StateProps {
  existingShowings: Showing[]
  availabilities?: ApplicantAvailability[]
  dateValueFromForm: Moment
  showings?: Showing[]
}

type ComponentProps = CustomCalendarProps &
  StateProps &
  WithTranslation &
  WithStyles

const CustomCalendar = ({
  input: { onChange },
  t,
  classes,
  existingShowings,
  availabilities,
  showings,
  dateValueFromForm
}: ComponentProps) => {
  moment.locale(getCurrentLanguage())
  const currentDay = moment()
    .set('hour', 0)
    .set('minute', 0)
    .set('second', 0)
    .set('millisecond', 0)
  const [selectedDay, setSelectedDay]: any = useState()
  /** use to control the calendar */
  const [selectedMonth, setSelectedMonth]: any = useState(
    moment().startOf('month')
  )
  const [futureAvaliablities, setFutureAvaliablities] = useState<
    number | string
  >('')
  const [pastAvaliablities, setPastAvaliablities] = useState<number | string>(
    ''
  )

  useEffect(() => {
    setFutureAvaliablities(getFutureCalendarAvaliablities)
    setPastAvaliablities(getPastCalendarAvaliablities)
  }, [selectedMonth])
  const setMonth = (action: 'back' | 'forward') => {
    if (action === 'back') {
      setSelectedMonth(moment(selectedMonth).subtract(1, 'M'))
    }
    if (action === 'forward') {
      setSelectedMonth(moment(selectedMonth).add(1, 'M'))
    }
  }
  const getFutureCalendarAvaliablities = (): any => {
    if (!availabilities) {
      return
    }
    const futureAvalibilities = getFutureAvalibilities(
      availabilities,
      selectedMonth
    )
    if (futureAvalibilities.length > 0) return futureAvalibilities.length
  }
  const getPastCalendarAvaliablities = (): any => {
    if (!availabilities) {
      return
    }
    const pastAvalibilities = getPastAvalibilities(
      availabilities,
      selectedMonth
    )
    if (pastAvalibilities.length > 0) return pastAvalibilities.length
  }

  const getDaysArray = (year: any, month: any) => {
    const monthIndex = month
    const date = new Date(year, monthIndex, 1)
    const startDate = moment(date).add(-1 * moment(date).weekday(), 'days')
    const endDate = moment(date).add(1, 'month')
    endDate.add(7 - endDate.weekday(), 'days')
    const result = []
    while (startDate < endDate) {
      result.push(startDate.clone())
      startDate.add(1, 'day')
    }
    if (result.length < 42) {
      const oldEndDate = endDate.clone()
      endDate.add(7 - endDate.weekday(), 'days')
      while (oldEndDate < endDate) {
        result.push(oldEndDate.clone())
        oldEndDate.add(1, 'day')
      }
    }
    return result
  }

  const handleOnChange = (date: any) => {
    setSelectedDay(date)
    if (date < selectedMonth) {
      setSelectedMonth(selectedMonth.clone().add(-1, 'month'))
    }
    if (date >= selectedMonth.clone().add(1, 'month')) {
      setSelectedMonth(selectedMonth.clone().add(1, 'month'))
    }
    onChange(date)
  }

  const getDateStatus = (day: any) => {
    const availabilityExists = () => {
      return availabilities?.find((existing) => {
        return (
          moment(existing.startDate).format('YYYY/MM/DD') ===
          day.format('YYYY/MM/DD')
        )
      })
    }
    const showingExists = () => {
      return showings?.find((existing) => {
        return (
          moment(existing.startDate).format('YYYY/MM/DD') ===
          day.format('YYYY/MM/DD')
        )
      })
    }
    if (selectedDay?.format('YYYY/MM/DD') === day.format('YYYY/MM/DD')) {
      return 'selectedDate'
    }
    if (moment(day).isBefore(currentDay)) {
      return 'disabledDate'
    }
    if (showingExists()) {
      return 'existingShowing'
    }
    if (availabilityExists()) {
      return 'existingDate'
    }
    return 'date'
  }

  const getClassForDate = (state: string) => {
    switch (state) {
      case 'selectedDate':
        return classes.selectedDate
      case 'disabledDate':
        return classes.disabledDate
      case 'existingShowing':
        return classes.existingShowing
      case 'existingDate':
        return classes.existingDate
      default:
        return classes.date
    }
  }

  return (
    <div className={classes.customCalendarContainer}>
      <Grid item xs={12} className={classes.container}>
        <div className={classes.arrows} onClick={() => setMonth('back')}>
          <LeftIcon />
        </div>
        <div className={classes.monthContainer}>
          {pastAvaliablities && (
            <div className={classes.pastAvaliabilities}>
              {pastAvaliablities}
            </div>
          )}
          <Typography variant='h5' className={classes.month}>
            {selectedMonth.format('MMMM')}
          </Typography>
          {futureAvaliablities && (
            <div className={classes.futureAvaliabilities}>
              {futureAvaliablities}
            </div>
          )}
        </div>

        <div className={classes.arrows} onClick={() => setMonth('forward')}>
          <RightIcon />
        </div>
      </Grid>

      <div className={classes.calendarContainer}>
        <div className={classes.horizontalList}>
          {Array(7)
            .fill(0)
            .map((_, weekday) => {
              const day = moment().set('weekday', weekday)
              return (
                <span key={weekday} className={classes.dateText}>
                  {day.format('ddd')}
                </span>
              )
            })}
          {getDaysArray(selectedMonth.year(), selectedMonth.month()).map(
            (day) => {
              const isThisMonth: boolean = day.month() === selectedMonth.month()
              const status: string = getDateStatus(day)
              const formattedDate = day.format('YYYY/MM/DD')
              return (
                <div
                  key={formattedDate}
                  className={
                    isThisMonth ? classes.listItem : classes.inActivelistItem
                  }
                  id={formattedDate}
                  onClick={() => {
                    if (
                      status !== 'selectedDate' &&
                      status !== 'disabledDate'
                    ) {
                      handleOnChange(day)
                    }
                  }}
                >
                  {formattedDate === currentDay.format('YYYY/MM/DD') && (
                    <span
                      className={
                        status === 'selectedDate'
                          ? classes.selectedToday
                          : classes.today
                      }
                    >
                      {t('general.today')}
                    </span>
                  )}
                  <div className={getClassForDate(status)}>
                    <span>{day.format('DD')}</span>
                  </div>
                </div>
              )
            }
          )}
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = (state: StateInterface) => {
  const apartmentId = selectContentIdFromPathname(state)

  const showingsInterface = apartmentId
    ? selectShowingsInterfaceByApartmentId(apartmentId)(state)
    : EmptyShowingsInterface
  const availabilities = apartmentId
    ? selectAvailabilitiesByApartment(apartmentId)(state)
    : undefined
  const selectedApplications = selectSelectedApplications(state)
  return {
    existingShowings: getFutureShowings(showingsInterface.showings, 'reserve'),
    dateValueFromForm: formValueSelector('AddShowingsForm')(state, 'date'),
    showings: formValueSelector('CreateShowingsForm')(state, 'showings'),
    availabilities:
      availabilities && selectedApplications
        ? selectAvailabilitiesByApplicationIds(
            availabilities,
            selectedApplications
          )
        : undefined
  }
}

export default connect(mapStateToProps)(
  withTranslation()(withStyles(styles)(CustomCalendar))
)
